import type { JSX } from 'react';

import { $isCodeHighlightNode } from '@lexical/code';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  getDOMSelection,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionIcon, Group, Stack } from '@mantine/core';
import { IconBold, IconItalic, IconStrikethrough, IconUnderline } from '@tabler/icons-react';
import { getDOMRangeRect } from '../utils/getDOMRangeRect';
import { getSelectedNode } from '../utils/getSelectedNode';
import { setFloatingElemPosition } from '../utils/setFloatingElemPosition';
import BackdropBlur from 'src/UI/common/BackdropBlur';
import { SiestaInlineInput } from './SiestaInlineInput';

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  enableInlineEditing,
  toolbarRef,
  isLoading,
  onLoadingChange,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isStrikethrough: boolean;
  isUnderline: boolean;
  enableInlineEditing: boolean;
  toolbarRef: (ref: HTMLElement | null) => void;
  isLoading: boolean;
  onLoadingChange: (isLoading: boolean) => void;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLElement | null>(null);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }
  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = getDOMSelection(editor._window);

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateTextFormatFloatingToolbar]);

  return (
    <BackdropBlur
      size="xxl"
      data-floating-toolbar
      ref={ref => {
        popupCharStylesEditorRef.current = ref;
        toolbarRef(ref);
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        width: enableInlineEditing ? 364 : undefined,
        borderRadius:
          'var(--mantine-spacing-xxs) var(--mantine-spacing-lg) var(--mantine-spacing-lg) var(--mantine-spacing-lg)',
        border: '1.5px solid #FF771C1A',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
      }}
    >
      {editor.isEditable() && (
        <Stack gap="xs" px="sm" py="xs">
          {!isLoading && (
            <Group gap="xxxs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color={isBold ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                title="Bold"
                aria-label="Format text as bold"
                size={28}
              >
                <IconBold size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isItalic ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                title="Italic"
                aria-label="Format text as italics"
                size={28}
              >
                <IconItalic size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isUnderline ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                title="Underline"
                aria-label="Format text to underlined"
                size={28}
              >
                <IconUnderline size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color={isStrikethrough ? 'brand.6' : 'black'}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                title="Strikethrough"
                aria-label="Format text with a strikethrough"
                size={28}
              >
                <IconStrikethrough size={18} />
              </ActionIcon>
            </Group>
          )}

          {enableInlineEditing && (
            <SiestaInlineInput
              editor={editor}
              onLoadingChange={onLoadingChange}
              isLoading={isLoading}
            />
          )}
        </Stack>
      )}
    </BackdropBlur>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  enableInlineEditing: boolean,
  externalIsLoading?: boolean,
  externalOnLoadingChange?: (loading: boolean) => void,
  hasInlineEdits?: boolean,
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const toolbarRef = useRef<HTMLElement | null>(null);

  const isLoading = externalIsLoading ?? internalIsLoading;
  isLoadingRef.current = isLoading;

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      isLoadingRef.current = loading;
      if (externalOnLoadingChange) {
        externalOnLoadingChange(loading);
      } else {
        setInternalIsLoading(loading);
      }
      if (!loading) {
        setIsText(false);
      }
    },
    [externalOnLoadingChange],
  );

  const updatePopup = useCallback(() => {
    const shouldKeepVisible = () => {
      return toolbarRef.current?.contains(document.activeElement) || isLoadingRef.current;
    };

    editor.getEditorState().read(() => {
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = getDOMSelection(editor._window);
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        if (!shouldKeepVisible()) {
          setIsText(false);
        }
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        if (!shouldKeepVisible()) {
          setIsText(false);
        }
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        if (!shouldKeepVisible()) {
          setIsText(false);
        }
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      }),
    );
  }, [editor, updatePopup]);

  if ((!isText && !isLoading) || hasInlineEdits) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isUnderline={isUnderline}
      enableInlineEditing={enableInlineEditing}
      toolbarRef={ref => {
        toolbarRef.current = ref;
      }}
      isLoading={isLoading}
      onLoadingChange={handleLoadingChange}
    />,
    anchorElem,
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
  enableInlineEditing = false,
  isInlineEditLoading,
  onInlineEditLoadingChange,
  hasInlineEdits = false,
}: {
  anchorElem?: HTMLElement;
  enableInlineEditing?: boolean;
  isInlineEditLoading?: boolean;
  onInlineEditLoadingChange?: (loading: boolean) => void;
  hasInlineEdits?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(
    editor,
    anchorElem,
    enableInlineEditing,
    isInlineEditLoading,
    onInlineEditLoadingChange,
    hasInlineEdits,
  );
}
